import { Schema, NodeSpec, MarkSpec, DOMOutputSpec, Node } from 'prosemirror-model'
import { addListNodes } from 'prosemirror-schema-list'
import OrderedMap from 'orderedmap'

const pDOM: DOMOutputSpec = ['p', 0]
const brDOM: DOMOutputSpec = ['br']

const nodes = {
  doc: {
    content: 'block+',
  } as NodeSpec,

  paragraph: {
    content: 'inline*',
    group: 'block',
    parseDOM: [{ tag: 'p' }],
    toDOM() {
      return pDOM
    },
  } as NodeSpec,

  text: {
    group: 'inline',
  } as NodeSpec,

  hard_break: {
    inline: true,
    group: 'inline',
    selectable: false,
    parseDOM: [{ tag: 'br' }],
    toDOM() {
      return brDOM
    },
  } as NodeSpec,

  pill: {
    inline: true,
    group: 'inline',
    attrs: {
      text: { default: '' },
    },
    atom: true,
    selectable: true,
    parseDOM: [
      {
        tag: 'pill',
        getAttrs: (dom) => {
          const domElement = dom as Element
          return {
            text: domElement.getAttribute('data-text'),
          }
        },
      },
    ],
    toDOM: (node) => {
      const {
        attrs: { text },
      } = node
      return ['pill', { 'data-text': text }, text]
    },
  } as NodeSpec,

  smart_link: {
    inline: true,
    group: 'inline',
    attrs: {
      type: { default: 'Article' },
      key: { default: '' },
      userId: { default: '' },
      title: { default: '' },
      url: { default: '' },
    },
    atom: true,
    selectable: true,
    parseDOM: [
      {
        tag: 'smart-link',
        getAttrs: (dom) => {
          const domElement = dom as Element
          return {
            type: domElement.getAttribute('data-type'),
            key: domElement.getAttribute('data-key'),
            userId: domElement.getAttribute('data-user-id'),
            title: domElement.getAttribute('data-title'),
            url: domElement.getAttribute('data-url'),
          }
        },
      },
    ],
    toDOM: (node) => {
      const {
        attrs: { type, key, userId, title, url },
      } = node
      return [
        'smart-link',
        { 'data-type': type, 'data-key': key, 'data-user-id': userId, 'data-title': title, 'data-url': url },
        `${type === 'User' ? '@' : ''}${title}`,
      ]
    },
  } as NodeSpec,

  heading: {
    attrs: { level: { default: 1 } },
    content: 'inline*',
    group: 'block',
    defining: true,
    parseDOM: [
      { tag: 'h1', attrs: { level: 1 } },
      { tag: 'h2', attrs: { level: 2 } },
      { tag: 'h3', attrs: { level: 3 } },
      { tag: 'h4', attrs: { level: 4 } },
      { tag: 'h5', attrs: { level: 5 } },
      { tag: 'h6', attrs: { level: 6 } },
    ],
    toDOM(node: Node) {
      return [`h${node.attrs.level}`, 0]
    },
  } as NodeSpec,
}

const emDOM: DOMOutputSpec = ['em', 0]
const strongDOM: DOMOutputSpec = ['strong', 0]
const codeDOM: DOMOutputSpec = ['code', 0]
const linkRel = 'nofollow noopener noreferrer'

function targetFromHref(href: string | null) {
  if (href != null && /^#/.test(href)) return '_self'
  return '_blank'
}

const marks = {
  link: {
    attrs: {
      href: {},
      title: { default: null },
      target: { default: '_blank' },
      rel: { default: linkRel },
    },
    inclusive: false,
    parseDOM: [
      {
        tag: 'a[href]',
        getAttrs(dom: HTMLElement) {
          const href = dom.getAttribute('href')
          return { href, title: dom.getAttribute('title'), target: targetFromHref(href), rel: linkRel }
        },
      },
    ],
    toDOM(node: Node) {
      const { href, title } = node.attrs
      return ['a', { href, title, target: targetFromHref(href), rel: linkRel }, 0]
    },
  } as unknown as MarkSpec,

  em: {
    parseDOM: [{ tag: 'i' }, { tag: 'em' }, { style: 'font-style=italic' }],
    toDOM() {
      return emDOM
    },
  } as MarkSpec,

  strong: {
    parseDOM: [
      { tag: 'strong' },
      { tag: 'b', getAttrs: (node: HTMLElement) => node.style.fontWeight !== 'normal' && null },
      { style: 'font-weight', getAttrs: (value: string) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null },
    ],
    toDOM() {
      return strongDOM
    },
  } as MarkSpec,

  code: {
    parseDOM: [{ tag: 'code' }],
    toDOM() {
      return codeDOM
    },
    excludes: 'em strong underline',
  } as MarkSpec,

  underline: {
    parseDOM: [{ tag: 'u' }],
    toDOM: () => ['u', 0],
  } as MarkSpec,
}

const basicSchema = new Schema({ nodes, marks })
export const brandProfileSchema = new Schema({
  nodes: addListNodes(basicSchema.spec.nodes as OrderedMap<NodeSpec>, 'paragraph block*', 'block'),
  marks: basicSchema.spec.marks,
})
